<template>
    <div class="acceptance-configuration-maintenance-page">
        <b-overlay :show="loading">
            <card title="Acceptance Configuration">
                <b-container fluid>
                    <b-row id="configuration-search-row" v-show="!editing">
                        <b-col>
                            <SearchComponent :id="acceptanceConfiguration && acceptanceConfiguration.id"
                                v-model="acceptanceConfiguration" label="Configuration"
                                search-u-r-l="/AcceptanceConfiguration" clearable :disabled="!disableFields" prepopulate />
                        </b-col>
                    </b-row>

                    <b-row id="name-status-row">
                        <b-col>
                            <b-form-group label="Name" :disabled="disableFields">
                                <b-form-input v-model="acceptanceConfiguration.name" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <enum-selector label="Status" v-model="acceptanceConfiguration.status"
                                :enum-codes="acceptanceConfigurationStatuses" :disabled="disableFields" />
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-form-group label="Default Expiration Mode">
                                <EnumSelector v-model="acceptanceConfiguration.defaultExpirationMode"
                                    :enum-codes="AcceptanceConfigurationExpirationMode" :disabled="disableFields"
                                    class="m-0" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group v-if="acceptanceConfiguration.defaultExpirationMode"
                                :label="defaultExpirationValueLabel">
                                <b-input v-model="acceptanceConfiguration.defaultExpirationModeValue" type="number"
                                    class="m-0" min="0"
                                    @blur="acceptanceConfiguration.defaultExpirationModeValue = !acceptanceConfiguration.defaultExpirationModeValue ? acceptanceConfiguration.defaultExpirationModeValue : Math.max(acceptanceConfiguration.defaultExpirationModeValue, 0)" />
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row id="checkbox-row">
                        <b-col>
                            <b-checkbox v-model="acceptanceConfiguration.mandatory" :disabled="disableFields">
                                Mandatory
                                <help-popup placement="top">
                                    When enabled, this acceptance must be completed in order to process orders. When disabled, the acceptance is preferred but not required.
                                </help-popup>
                            </b-checkbox>
                        </b-col>
                        <b-col>
                            <b-checkbox v-model="acceptanceConfiguration.recurring" :disabled="disableFields">
                                Recurring
                                <help-popup placement="top">
                                    When enabled, this acceptance is required for every order. When disabled, the acceptance is only required once per patient.
                                </help-popup>
                            </b-checkbox>
                        </b-col>
                        <b-col>
                            <b-checkbox v-model="acceptanceConfiguration.requiresSignature" :disabled="disableFields">
                                Requires Signature
                                <help-popup placement="top">
                                    When enabled, requires the patient to provide a digital signature when accepting. When disabled, verbal confirmation is sufficient.
                                </help-popup>
                            </b-checkbox>
                        </b-col>
                    </b-row>

                    <hr />
                    <b-row id="image-row">
                        <b-col>
                            <b-row>
                                <b-col>
                                    Image: {{ acceptanceConfiguration.imageId }}
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    <b-button v-b-modal.form-modal variant="info">Update Form</b-button>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                    <b-modal id="form-modal" title="Update Form" @ok="updateForm">
                        <b-row>
                            <b-col>
                                <label for="upload-file">Upload File</label>
                                <b-form-file id="upload-file" v-model="newDocument" accept=".pdf"
                                    drop-placeholder="Drop file here..."
                                    placeholder="Choose the pdf associated with this acceptance requirement" />
                            </b-col>
                        </b-row>
                    </b-modal>

                    <hr />
                    <b-row id="linked-entities-row">
                        <b-col>
                            <b-row id="add-entity-row" align-v="end">
                                <b-col>
                                    <enum-selector label="Entity Type" v-model="selectedEntityType"
                                        :enum-codes="entityTypes" :elements-to-discard="discardTypes" clearable
                                        :disabled="disableFields" />
                                </b-col>
                                <b-col>
                                    <SearchComponent :id="entitySearch && +entitySearch.id"
                                        :disabled="disableEntitySearch" v-model="entitySearch" :label="entityLabel"
                                        :search-u-r-l="entitySearchUrl" :option-label="optionLabel" clearable />
                                </b-col>
                                <b-col>
                                    <b-button @click="addEntity" variant="success">Add Entity</b-button>
                                </b-col>
                            </b-row>
                            <b-row id="linked-table-row">
                                <b-col>
                                    <b-table title="Linked Entities" :items="acceptanceConfiguration.linkedEntities"
                                        :fields="entityFields" show-empty empty-text="No Linked Entities Added Yet">
                                        <template #cell(actions)="row">
                                            <b-button variant="danger" size="sm" @click="removeEntity(row.item)">x
                                            </b-button>
                                        </template>
                                    </b-table>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>

                </b-container>
                <NewClearSaveButtons :disable-clear="disableFields" :titles="buttonTitles" :disable-save="disableFields"
                    @save-clicked="submitConfiguration" @clear-clicked="clearConfiguration" @new-clicked="newClicked" />
            </card>
        </b-overlay>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import SearchComponent from "@/components/SearchComponent.vue";
import { AcceptanceConfiguration, AcceptanceConfigurationExpirationMode, AcceptanceConfigurationStatus, LinkedEntity } from "@/models/AcceptanceConfiguration";
import EnumSelector from "@/components/EnumSelector.vue";
import axios, { AxiosRequestConfig } from "axios";
import NewClearSaveButtons, { NewClearSaveTitles } from "@/components/NewClearSaveButtons.vue";
import { Program } from "@/models/Program";
import { Drug } from "@/models/Drug/Drug";
import { NonGSDDDrug } from "@/models/Drug/NonGSDDDrug";
import { CompoundDrug } from "@/models/Drug/CompoundDrug";
import HasLabel from "@/models/HasLabel";
import HasID from "@/models/HasID";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { BvTableFieldArray } from "bootstrap-vue";
import { EntityType } from "@/models/EntityType";
import HelpPopup from '@/components/HelpPopup.vue';

@Component({
    name: "AcceptanceConfigurationMaintenancePage",
    components: { NewClearSaveButtons, EnumSelector, SearchComponent, HelpPopup },
})
export default class AcceptanceConfigurationMaintenancePage extends Vue {
    private acceptanceConfiguration?: AcceptanceConfiguration = new AcceptanceConfiguration();
    protected acceptanceConfigurationStatuses = AcceptanceConfigurationStatus;
    protected entityTypes = EntityType;
    private entitySearch: HasLabel & HasID = {} as any;

    private selectedEntityType: EntityType | null = null;

    private loading = false;
    private newMode = false;

    private newDocument: File = new File([], "");

    protected AcceptanceConfigurationExpirationMode = AcceptanceConfigurationExpirationMode;

    get defaultExpirationValueLabel() {
        switch (this.acceptanceConfiguration?.defaultExpirationMode) {
            case AcceptanceConfigurationExpirationMode.NumberOfDays:
                return "Number of Days";
            case AcceptanceConfigurationExpirationMode.NumbersOfYears:
                return "Number of Years";
            case AcceptanceConfigurationExpirationMode.CalendarYear:
                return "Number of Calendar Years";
        }
        return "";
    }

    @Watch("acceptanceConfiguration.defaultExpirationMode")
    defaultExpirationModeChange() {
        switch (this.acceptanceConfiguration?.defaultExpirationMode) {
            case AcceptanceConfigurationExpirationMode.NumberOfDays:
                this.acceptanceConfiguration.defaultExpirationModeValue = 90;
                break;
            case AcceptanceConfigurationExpirationMode.NumbersOfYears:
                this.acceptanceConfiguration.defaultExpirationModeValue = 1;
                break;
            case AcceptanceConfigurationExpirationMode.CalendarYear:
                this.acceptanceConfiguration.defaultExpirationModeValue = 1;
                break;
        }
    }

    get discardTypes(): EntityType[] {
        const allowedEntityTypes: EntityType[] = [
            EntityType.Program,
            EntityType.GsddDrug,
            EntityType.NonGsddDrug,
            EntityType.CompoundDrug
        ];

        const allEntityTypes = Object.keys(EntityType)
            .filter(key => isNaN(Number(key)))
            .map(key => EntityType[key as keyof typeof EntityType]);

        const discardTypes = allEntityTypes.filter(
            type => !allowedEntityTypes.includes(type)
        );

        return discardTypes;
    }

    get routeId(): number {
        const res = parseInt(this.$route.params.id);
        if (isNaN(res)) return 0;
        return res;
    }

    get isFormValid(): boolean {
        return true;
    }

    get editing(): boolean {
        return !!this.routeId || this.newMode;
    }

    get buttonTitles(): NewClearSaveTitles {
        if (this.editing) {
            return {
                save: "Save",
                clear: "Clear",
                new: "",
                cancel: "",
            };
        }

        return {
            new: "New Configuration",
            clear: "Clear",
            save: "Save Changes",
            cancel: "",
        };
    }

    get disableFields(): boolean {
        return !this.editing;
    }

    get disableEntitySearch(): boolean {
        const haveEntityType = this.selectedEntityType != null;
        //console.log("haveEntityType: ", haveEntityType);
        return !haveEntityType;
    }

    get entityLabel(): string {
        if (this.selectedEntityType == null) return "Search";
        return `${EntityType[this.selectedEntityType]}`;
    }

    get entityFields(): BvTableFieldArray {
        return [
            { key: "name" },
            { key: "entityId", },
            { key: "entityType", formatter: value => EntityType[value] },
            { key: "actions", label: "", },
        ];
    }

    get entitySearchUrl(): string {
        switch (this.selectedEntityType) {
            case EntityType.Program:
                return "/api/Program";
            case EntityType.GsddDrug:
                return "/api/Drug";
            case EntityType.CompoundDrug:
                return "/api/Compound";
            case EntityType.NonGsddDrug:
                return "/api/NonGsdd";
        }
        return "";
    }

    created() {
        if (this.routeId) this.fetchConfiguration({ id: this.routeId } as AcceptanceConfiguration);
    }

    fetchConfiguration(configuration: AcceptanceConfiguration) {
        this.loading = true;
        axios.get<AcceptanceConfiguration>(`/api/AcceptanceConfiguration/${configuration.id}`)
            .then(res => {
                this.acceptanceConfiguration = new AcceptanceConfiguration(res.data);
            })
            .catch(err => this.$notification(NotificationOptions.error(err)))
            .finally(() => {
                this.loading = false;
            });
    }

    async submitConfiguration() {
        try {
            const resp = await axios.post(`/api/AcceptanceConfiguration`, this.acceptanceConfiguration);
            this.acceptanceConfiguration = new AcceptanceConfiguration(resp.data);
            this.$notification(NotificationOptions.successSaveNotificationPreset("Acceptance Configuration"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    updateForm() {
        if (!this.acceptanceConfiguration?.id) {
            this.$notification(NotificationOptions.error("You Must Save Before You Can Update The Form"));
            return;
        }

        const formData = new FormData();
        formData.append('document', this.newDocument);
        formData.append('fileName', this.acceptanceConfiguration.name.trim());

        const url = `/api/AcceptanceConfiguration/${this.acceptanceConfiguration.id}/UpdateForm`;
        let config: AxiosRequestConfig = { headers: { 'Content-Type': 'multipart/form-data' } };
        axios.post<AcceptanceConfiguration>(url, formData, config)
            .then(resp => {
                this.acceptanceConfiguration = new AcceptanceConfiguration(resp.data);
                this.$notification(NotificationOptions.successSaveNotificationPreset("Form Update"))
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            })
    }

    newClicked() {
        this.newMode = true;
    }

    clearConfiguration() {
        this.newMode = false;
        this.acceptanceConfiguration = undefined;
    }

    removeEntity(item: LinkedEntity) {
        console.log("remove the entity");
        axios.post<AcceptanceConfiguration>(`/api/AcceptanceConfiguration/${this.acceptanceConfiguration?.id}/Unlink`, item)
            .then(resp => {
                this.acceptanceConfiguration = new AcceptanceConfiguration(resp.data);
                this.selectedEntityType = null;
                this.entitySearch = {} as any;
                this.$notification(NotificationOptions.successSaveNotificationPreset("Removed Entity"));
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            });
    }

    addEntity() {
        if (!this.acceptanceConfiguration?.id) {
            this.$notification(NotificationOptions.error("You Must Save Before You Can Link Entities"));
            return;
        }
        console.log("link the entity", this.selectedEntityType, this.entitySearch);
        const obj = this.objectifiedSearch(this.entitySearch);
        if (this.selectedEntityType == null) {
            this.$notification(NotificationOptions.error("You Must Select an Entity Type First"));
            return;
        }
        if (obj?.id == null) {
            this.$notification(NotificationOptions.error("You Must Select an Entity First"));
            return;
        }

        const data: LinkedEntity = {
            entityId: +obj.id,
            entityType: this.selectedEntityType,
        };

        axios.post<AcceptanceConfiguration>(`/api/AcceptanceConfiguration/${this.acceptanceConfiguration?.id}/Link`, data)
            .then(resp => {
                this.acceptanceConfiguration = new AcceptanceConfiguration(resp.data);
                this.selectedEntityType = null;
                this.entitySearch = {} as any;
                this.$notification(NotificationOptions.successSaveNotificationPreset("Linked Entity"));
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            })
    }

    objectifiedSearch(o: any): (HasLabel & HasID) | null {
        let obj: (HasLabel & HasID) | null = null;
        switch (this.selectedEntityType) {
            case EntityType.Program:
                obj = new Program(o);
                break;
            case EntityType.GsddDrug:
                obj = new Drug(null, o);
                break;
            case EntityType.NonGsddDrug:
                obj = new NonGSDDDrug(null, o);
                break;
            case EntityType.CompoundDrug:
                obj = new CompoundDrug(null, o);
                break;
        }
        return obj;
    }

    optionLabel(o: any): string {
        let obj = this.objectifiedSearch(o);
        if (obj == null) return "";
        return obj.getLabel();
    }

    @Watch('acceptanceConfiguration')
    onConfigurationChange(value: AcceptanceConfiguration) {
        if (!value) {
            this.acceptanceConfiguration = new AcceptanceConfiguration();
            this.selectedEntityType = null;
            this.entitySearch = {} as any;
            return;
        }
        if (!value?.id) {
            if (this.routeId) this.$router.push({ name: 'AcceptanceConfiguration' });
            return;
        }
        if (value.id && value.id != this.routeId) {
            this.$router.push({ name: 'AcceptanceConfigurationDetails', params: { id: value.id.toString() } })
        }
    }

    @Watch('entitySearch')
    onEntitySearchChange(value: any) {
        console.log("entity: ", value);
    }

    @Watch('selectedEntityType')
    onEntityTypeChange(value: EntityType) {
        switch (value) {
            case EntityType.Program:
                this.entitySearch = new Program();
                break;
            case EntityType.GsddDrug:
                this.entitySearch = new Drug();
                break;
            case EntityType.NonGsddDrug:
                this.entitySearch = new NonGSDDDrug();
                break;
            case EntityType.CompoundDrug:
                this.entitySearch = new CompoundDrug();
                break;
        }
    }

}
</script>

<style scoped></style>
